import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/base/MovieCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import { moviesData } from '@/mocks/movies';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const movie = useMemo(() => moviesData.find(m => m.id === Number(id)), [id]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  if (!movie) {
    return (
      <>
        <header><Navbar /></header>
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-full">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-900 font-heading mb-2">内容未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您要查看的内容不存在或已下架。</p>
            <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedMovies = moviesData
    .filter(m => m.id !== movie.id && (m.category === movie.category || m.subCategory === movie.subCategory))
    .slice(0, 5);

  return (
    <>
      <header>
        <Navbar />
      </header>

      <main className="pt-16">
        {/* Breadcrumb */}
        <div className="bg-background-100 py-3">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: movie.category, href: `/category/${movie.category === '电影' ? 'movie' : movie.category === '电视剧' ? 'tv' : movie.category === '综艺' ? 'variety' : 'anime'}` },
              { label: movie.title },
            ]}
          />
        </div>

        {/* Article Hero */}
        <section className="relative overflow-hidden bg-background-900">
          <div className="absolute inset-0">
            <img
              src={movie.imageUrl.replace('width=600&height=800', 'width=1600&height=900')}
              alt={movie.title}
              className="w-full h-full object-cover object-top opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-background-900 via-background-900/80 to-background-900/40"></div>
          </div>
          <div className="relative w-full px-4 md:px-6 py-10 md:py-14">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-shrink-0 w-48 md:w-56 mx-auto lg:mx-0">
                <div className="aspect-[3/4] rounded-lg overflow-hidden">
                  <img
                    src={movie.imageUrl}
                    alt={movie.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-bold text-background-50 font-heading mb-3">
                  {movie.title}
                </h1>
                {movie.titleEn && (
                  <p className="text-background-400 text-sm mb-4">{movie.titleEn}</p>
                )}
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="flex items-center gap-1 text-accent-400">
                    <i className="ri-star-fill"></i>
                    <strong className="text-lg">{movie.rating}</strong>
                  </span>
                  <span className="text-background-300 text-sm">{movie.year}</span>
                  <span className="text-background-300 text-sm">{movie.region}</span>
                  <span className="bg-background-50/15 text-background-200 px-2 py-0.5 rounded text-xs whitespace-nowrap">
                    {movie.category}
                  </span>
                  <span className="bg-background-50/15 text-background-200 px-2 py-0.5 rounded text-xs whitespace-nowrap">
                    {movie.subCategory}
                  </span>
                  {movie.duration && (
                    <span className="text-background-300 text-sm">{movie.duration}</span>
                  )}
                  {movie.updateEpisode && (
                    <span className="text-accent-400 text-sm font-medium">{movie.updateEpisode}</span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2 mb-5">
                  <span className="text-background-400 text-sm whitespace-nowrap">导演：</span>
                  <span className="text-background-200 text-sm">{movie.director}</span>
                </div>
                <div className="flex flex-wrap items-center gap-2 mb-5">
                  <span className="text-background-400 text-sm whitespace-nowrap">主演：</span>
                  <span className="text-background-200 text-sm">{movie.actors.join(' / ')}</span>
                </div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="flex items-center gap-1 text-background-400 text-sm">
                    <i className="ri-eye-line"></i>
                    <span>{(movie.views / 10000).toFixed(0)}万次观看</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-lg font-medium text-sm transition-all hover:bg-primary-600 whitespace-nowrap cursor-pointer">
                    <i className="ri-play-circle-fill text-lg"></i>
                    立即播放
                  </button>
                  <button className="inline-flex items-center gap-2 px-4 py-3 bg-background-50/10 text-background-200 rounded-lg text-sm hover:bg-background-50/20 transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-heart-line"></i>
                    收藏
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Description */}
        <section className="py-8 md:py-10 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-4xl">
            <h2 className="text-lg font-bold text-foreground-900 font-heading mb-4">剧情简介</h2>
            <p className="text-foreground-700 leading-relaxed text-sm">{movie.description}</p>
          </div>
        </section>

        {/* Related */}
        <section className="py-8 md:py-10 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <h2 className="text-lg font-bold text-foreground-900 font-heading mb-6">相关推荐</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop>
              {relatedMovies.map((movie) => (
                <MovieCard key={movie.id} {...movie} />
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {/* Article Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": movie.title,
            "description": movie.description,
            "image": movie.imageUrl,
            "datePublished": `${movie.year}-01-01`,
            "author": {
              "@type": "Person",
              "name": movie.director
            }
          })
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "首页", "item": typeof window !== 'undefined' ? window.location.origin + '/' : '/' },
              { "@type": "ListItem", "position": 2, "name": movie.category, "item": typeof window !== 'undefined' ? `${window.location.origin}/category/${movie.category === '电影' ? 'movie' : movie.category === '电视剧' ? 'tv' : movie.category === '综艺' ? 'variety' : 'anime'}` : '/' },
              { "@type": "ListItem", "position": 3, "name": movie.title },
            ]
          })
        }}
      />
    </>
  );
}